import React, { useState, useEffect, useRef } from 'react';
import { Palette, Image as ImageIcon, Sliders, Type, Save, MousePointerClick, Sparkles } from 'lucide-react';
import ColorThief from 'colorthief';
import { supabase } from '../lib/supabase';
import { v4 as uuidv4 } from 'uuid';
import type { ThemeConfig, IntroAnimationType } from '../types/theme';
import { INTRO_ANIMATION_OPTIONS } from '../types/theme';

interface Props {
  registryId: string;
  initialTheme?: ThemeConfig;
  onThemeChange: (theme: ThemeConfig) => void;
}

export default function ThemeCustomizer({ registryId, initialTheme, onThemeChange }: Props) {
  const [theme, setTheme] = useState<ThemeConfig>(initialTheme || {
    backgroundImage: '',
    textColor: '#ffffff',
    backgroundColor: 'rgba(0, 0, 0, 0.75)',
    opacity: 0.75,
    fontFamily: 'IntroRust',
    buttonColor: '#a855f7',
    buttonTextColor: '#ffffff',
    introAnimation: 'none',
    introTitle: '',
    introSubtitle: '',
  });
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');

  const fonts = [
    { name: 'IntroRust', label: 'Default' },
    { name: 'Playfair Display', label: 'Elegant' },
    { name: 'Montserrat', label: 'Modern' },
    { name: 'Lora', label: 'Classic' },
    { name: 'Quicksand', label: 'Light' },
    { name: 'Roboto', label: 'Clean' },
    { name: 'Merriweather', label: 'Traditional' },
    { name: 'Poppins', label: 'Contemporary' },
    { name: 'Dancing Script', label: 'Cursive' },
    { name: 'Great Vibes', label: 'Elegant Script' },
    { name: 'Oswald', label: 'Bold' },
    { name: 'Raleway', label: 'Minimalist' }
  ];

  useEffect(() => {
    const link = document.createElement('link');
    link.href = 'https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600&family=Montserrat:wght@400;600&family=Lora:wght@400;600&family=Quicksand:wght@400;600&family=Roboto:wght@400;500&family=Merriweather:wght@400;700&family=Poppins:wght@400;600&family=Dancing+Script:wght@400;700&family=Great+Vibes&family=Oswald:wght@400;600&family=Raleway:wght@400;600&display=swap';
    link.rel = 'stylesheet';
    document.head.appendChild(link);
    return () => link.remove();
  }, []);

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setSaveStatus('saving');

      // Create a new image for processing
      const img = new Image();
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      // Load the image
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = URL.createObjectURL(file);
      });

      // Calculate dimensions for resizing
      const maxDimension = 1920; // Max width/height
      let width = img.width;
      let height = img.height;

      if (width > maxDimension || height > maxDimension) {
        if (width > height) {
          height = (height / width) * maxDimension;
          width = maxDimension;
        } else {
          width = (width / height) * maxDimension;
          height = maxDimension;
        }
      }

      // Set canvas dimensions and draw resized image
      canvas.width = width;
      canvas.height = height;
      ctx?.drawImage(img, 0, 0, width, height);

      // Convert to blob with compression
      const blob = await new Promise<Blob>((resolve) => {
        canvas.toBlob((b) => resolve(b!), 'image/jpeg', 0.85);
      });

      // Upload to Supabase Storage
      const fileName = `${uuidv4()}.jpg`;
      const filePath = `backgrounds/${fileName}`;

      const { error: uploadError, data } = await supabase.storage
        .from('registry-images')
        .upload(filePath, blob, {
          cacheControl: '31536000', // Cache for 1 year
          upsert: false,
          contentType: 'image/jpeg'
        });

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('registry-images')
        .getPublicUrl(filePath);

      // Analyze colors
      const colorThief = new ColorThief();
      const dominantColor = colorThief.getColor(img);
      const [r, g, b] = dominantColor;
      
      // Calculate brightness and set theme
      const brightness = (r * 299 + g * 587 + b * 114) / 1000;
      const newTheme = {
        ...theme,
        backgroundImage: publicUrl,
        textColor: brightness > 128 ? '#000000' : '#ffffff',
        backgroundColor: `rgba(${brightness > 128 ? '0,0,0' : '255,255,255'}, ${theme.opacity})`
      };
      
      setTheme(newTheme);
      onThemeChange(newTheme);
      setSaveStatus('idle');

    } catch (error) {
      console.error('Error processing image:', error);
      setSaveStatus('error');
    }
  };

  const handleOpacityChange = (value: number) => {
    const newTheme = {
      ...theme,
      opacity: value,
      backgroundColor: `rgba(${theme.textColor === '#ffffff' ? '0,0,0' : '255,255,255'}, ${value})`
    };
    setTheme(newTheme);
    onThemeChange(newTheme);
    setSaveStatus('idle');
  };

  const handleFontChange = (fontFamily: string) => {
    const newTheme = { ...theme, fontFamily };
    setTheme(newTheme);
    onThemeChange(newTheme);
    setSaveStatus('idle');
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setSaveStatus('saving');

      const themeData = {
        backgroundImage: theme.backgroundImage || '',
        textColor: theme.textColor || '#ffffff',
        backgroundColor: theme.backgroundColor || 'rgba(0, 0, 0, 0.75)',
        opacity: theme.opacity || 0.75,
        fontFamily: theme.fontFamily || 'IntroRust',
        buttonColor: theme.buttonColor || '#a855f7',
        buttonTextColor: theme.buttonTextColor || '#ffffff',
        introAnimation: theme.introAnimation || 'none',
        introTitle: theme.introTitle || '',
        introSubtitle: theme.introSubtitle || '',
      };

      // Read existing theme to preserve layout data
      const { data: existing, error: readError } = await supabase
        .from('registries')
        .select('theme')
        .eq('id', registryId)
        .maybeSingle();

      if (readError || !existing) throw readError || new Error("Unable to load the current theme");
      const existingTheme = typeof existing?.theme === 'object' && existing.theme ? existing.theme : {};
      const merged = { ...existingTheme, ...themeData };

      const { error } = await supabase
        .from('registries')
        .update({ theme: merged })
        .eq('id', registryId);

      if (error) throw error;
      setSaveStatus('saved');

      setTimeout(() => {
        setSaveStatus('idle');
      }, 2000);
    } catch (err) {
      console.error('Error saving theme:', err);
      setSaveStatus('error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed bottom-4 right-4 z-50">
      <div className="card-pastel p-4 shadow-soft-lg max-h-[calc(100vh-2rem)] overflow-y-auto">
        <div className="space-y-4">
          <div>
            <label className="flex items-center space-x-2 text-gray-800 mb-2 font-poppins">
              <ImageIcon className="w-4 h-4" />
              <span>Background Image</span>
            </label>
            <input
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              className="hidden"
              id="background-upload"
            />
            <label
              htmlFor="background-upload"
              className="block w-full px-4 py-2 btn-pastel text-white rounded-md cursor-pointer text-center"
            >
              Upload Image
            </label>
          </div>

          <div>
            <label className="flex items-center space-x-2 text-gray-800 mb-2 font-poppins">
              <Palette className="w-4 h-4" />
              <span>Text Color</span>
            </label>
            <input
              type="color"
              value={theme.textColor}
              onChange={(e) => {
                const newTheme = { ...theme, textColor: e.target.value };
                setTheme(newTheme);
                onThemeChange(newTheme);
                setSaveStatus('idle');
              }}
              className="block w-full h-8 rounded-md cursor-pointer"
            />
          </div>

          <div>
            <label className="flex items-center space-x-2 text-gray-800 mb-2 font-poppins">
              <MousePointerClick className="w-4 h-4" />
              <span>Button Color</span>
            </label>
            <input
              type="color"
              value={theme.buttonColor || '#a855f7'}
              onChange={(e) => {
                const newTheme = { ...theme, buttonColor: e.target.value };
                setTheme(newTheme);
                onThemeChange(newTheme);
                setSaveStatus('idle');
              }}
              className="block w-full h-8 rounded-md cursor-pointer"
            />
          </div>

          <div>
            <label className="flex items-center space-x-2 text-gray-800 mb-2 font-poppins">
              <Palette className="w-4 h-4" />
              <span>Button Text Color</span>
            </label>
            <input
              type="color"
              value={theme.buttonTextColor || '#ffffff'}
              onChange={(e) => {
                const newTheme = { ...theme, buttonTextColor: e.target.value };
                setTheme(newTheme);
                onThemeChange(newTheme);
                setSaveStatus('idle');
              }}
              className="block w-full h-8 rounded-md cursor-pointer"
            />
          </div>

          <div>
            <label className="flex items-center space-x-2 text-gray-800 mb-2 font-poppins">
              <Sliders className="w-4 h-4" />
              <span>Background Opacity</span>
            </label>
            <input
              type="range"
              min="0"
              max="1"
              step="0.05"
              value={theme.opacity}
              onChange={(e) => handleOpacityChange(parseFloat(e.target.value))}
              className="w-full"
            />
          </div>

          <div>
            <label className="flex items-center space-x-2 text-gray-800 mb-2 font-poppins">
              <Type className="w-4 h-4" />
              <span>Font Style</span>
            </label>
            <select
              value={theme.fontFamily}
              onChange={(e) => handleFontChange(e.target.value)}
              className="input-pastel"
            >
              {fonts.map((font) => (
                <option key={font.name} value={font.name} style={{ fontFamily: font.name }}>
                  {font.label}
                </option>
              ))}
            </select>
          </div>

          <div className="pt-2 border-t border-gray-200">
            <label className="flex items-center space-x-2 text-gray-800 mb-2 font-poppins">
              <Sparkles className="w-4 h-4" />
              <span>Opening Animation</span>
            </label>
            <select
              value={theme.introAnimation || 'none'}
              onChange={(e) => {
                const newTheme = { ...theme, introAnimation: e.target.value as IntroAnimationType };
                setTheme(newTheme);
                onThemeChange(newTheme);
                setSaveStatus('idle');
              }}
              className="input-pastel"
            >
              {INTRO_ANIMATION_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {(theme.introAnimation || 'none') !== 'none' && (
            <>
              <div>
                <label className="flex items-center space-x-2 text-gray-800 mb-2 font-poppins">
                  <Type className="w-4 h-4" />
                  <span>Intro Title</span>
                </label>
                <input
                  type="text"
                  value={theme.introTitle || ''}
                  onChange={(e) => {
                    const newTheme = { ...theme, introTitle: e.target.value };
                    setTheme(newTheme);
                    onThemeChange(newTheme);
                    setSaveStatus('idle');
                  }}
                  placeholder="e.g. You're Invited!"
                  className="input-pastel"
                />
              </div>
              <div>
                <label className="flex items-center space-x-2 text-gray-800 mb-2 font-poppins">
                  <Type className="w-4 h-4" />
                  <span>Intro Subtitle</span>
                </label>
                <input
                  type="text"
                  value={theme.introSubtitle || ''}
                  onChange={(e) => {
                    const newTheme = { ...theme, introSubtitle: e.target.value };
                    setTheme(newTheme);
                    onThemeChange(newTheme);
                    setSaveStatus('idle');
                  }}
                  placeholder="e.g. We invite you to celebrate with us"
                  className="input-pastel"
                />
              </div>
            </>
          )}

          <button
            onClick={handleSave}
            disabled={saving || saveStatus === 'saved'}
            className={`w-full flex items-center justify-center space-x-2 px-4 py-2 rounded-md transition-colors ${
              saveStatus === 'saved'
                ? 'bg-green-600 text-white'
                : saveStatus === 'error'
                ? 'bg-red-600 text-white'
                : 'btn-pastel text-white'
            }`}
          >
            <Save className="w-4 h-4" />
            <span>
              {saveStatus === 'saving'
                ? 'Saving...'
                : saveStatus === 'saved'
                ? 'Saved!'
                : saveStatus === 'error'
                ? 'Error!'
                : 'Save Changes'}
            </span>
          </button>
        </div>
      </div>
    </div>
  );
}
